import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { GetApiIntegrationDto, userState } from "./users.types";
import {
  getAPIIntegrations,
  getMe,
  getPublicUser,
  registerAPIIntegration,
  removeAPIIntegration,
  requestPasswordResetToken,
  requestVerificationToken,
  resetPassword,
  updateUser,
  verifyAccount,
} from "./users.thunk";
import {
  endNoteRecord,
  startNoteRecord,
  togglePauseTimer,
} from "../tasks/taskThunk";

const initialState: userState = {
  userData: {
    email: "",
    firstName: "",
    lastName: "",
    token: "",
    isVerified: false,
    membership: "Free",
    userName: "",
  },

  userAPIConnections: [],
  isError: false,
  isSuccess: false,
  isLoading: false,
  message: "",
};

export const userSlice = createSlice({
  name: "userManagement",
  initialState,
  reducers: {
    resetUserStatus: (state) => {
      state.isLoading = false;
      state.isSuccess = false;
      state.message = "";
      state.isError = false;
    },
    setActiveTask: (
      state,
      action: PayloadAction<{ taskId: string | null }>,
    ) => {
      state.userData.activeTask = { id: action.payload.taskId };
    },
  },
  extraReducers: (builder) => {
    builder

      // External
      .addCase(endNoteRecord.fulfilled, (state) => {
        state.userData.activeTask = { id: null };
      })
      .addCase(startNoteRecord.fulfilled, (state, action) => {
        state.userData.activeTask = { id: action.payload.noteId || null };
      })
      .addCase(togglePauseTimer.fulfilled, (state, action) => {
        if (action.payload.endedAt) {
          // Ended a pause
          state.userData.activeRecordingId = null;
        } else {
          // Started a pause
          state.userData.activeRecordingId = action.payload.id;
        }
      })

      // Internal

      .addCase(getMe.fulfilled, (state, action) => {
        const {
          microsoftCalendarConnection,
          googleCalendarConnection,
          ...payload
        } = action.payload;

        state.isLoading = false;
        state.isSuccess = true;
        state.userData = payload;
      })
      .addCase(getMe.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload as string;
      })
      .addCase(getMe.pending, (state) => {
        state.isLoading = true;
        state.isSuccess = false;
      })

      .addCase(getPublicUser.fulfilled, (state, action) => {
        const { ...payload } = action.payload;
        state.isLoading = false;
        state.isSuccess = true;
        state.publicUser = payload;
      })
      .addCase(getPublicUser.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload as string;
      })
      .addCase(getPublicUser.pending, (state) => {
        state.isLoading = true;
        state.isSuccess = false;
      })

      // API Integration
      .addCase(getAPIIntegrations.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.userAPIConnections = action.payload;
      })
      .addCase(getAPIIntegrations.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload as string;
      })
      .addCase(getAPIIntegrations.pending, (state) => {
        state.isLoading = true;
        state.isSuccess = false;
      })
      .addCase(registerAPIIntegration.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;

        const merged = new Map<string, GetApiIntegrationDto>();

        // Add existing connections first
        state.userAPIConnections.forEach((con) => {
          merged.set(con.id, con);
        });

        // Then override or add new ones from the payload
        action.payload.forEach((newCon) => {
          merged.set(newCon.id, newCon);
        });

        state.userAPIConnections = Array.from(merged.values());
      })
      .addCase(registerAPIIntegration.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload as string;
      })
      .addCase(registerAPIIntegration.pending, (state) => {
        state.isLoading = true;
        state.isSuccess = false;
      })

      .addCase(removeAPIIntegration.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        console.log(action.payload);
        state.userAPIConnections = state.userAPIConnections.filter(
          (a) => a.id !== action.payload,
        );
      })
      .addCase(removeAPIIntegration.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload as string;
      })
      .addCase(removeAPIIntegration.pending, (state) => {
        state.isLoading = true;
        state.isSuccess = false;
      })

      .addCase(verifyAccount.fulfilled, (state, action) => {
        const {
          microsoftCalendarConnection,
          googleCalendarConnection,
          ...payload
        } = action.payload;

        state.isLoading = false;
        state.isSuccess = true;
        state.userData = payload;
      })
      .addCase(verifyAccount.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload as string;
      })
      .addCase(verifyAccount.pending, (state) => {
        state.isLoading = true;
        state.isSuccess = false;
      })
      .addCase(requestVerificationToken.fulfilled, (state) => {
        state.isLoading = false;
        state.isSuccess = true;
      })
      .addCase(requestVerificationToken.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload as string;
      })
      .addCase(requestVerificationToken.pending, (state) => {
        state.isLoading = true;
        state.isSuccess = false;
      })
      .addCase(requestPasswordResetToken.fulfilled, (state) => {
        state.isLoading = false;
        state.isSuccess = true;
      })
      .addCase(requestPasswordResetToken.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload as string;
      })
      .addCase(requestPasswordResetToken.pending, (state) => {
        state.isLoading = true;
        state.isSuccess = false;
      })
      .addCase(resetPassword.fulfilled, (state) => {
        state.isLoading = false;
        state.isSuccess = true;
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload as string;
      })
      .addCase(resetPassword.pending, (state) => {
        state.isLoading = true;
        state.isSuccess = false;
      })
      .addCase(updateUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.userData = action.payload;
      })
      .addCase(updateUser.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload as string;
      })
      .addCase(updateUser.pending, (state) => {
        state.isLoading = true;
        state.isSuccess = false;
      });
  },
});

export const { resetUserStatus, setActiveTask } = userSlice.actions;
export default userSlice.reducer;
